import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { AiOutlineDownload } from 'react-icons/ai';
import { FiExternalLink, FiMail, FiShield, FiFileText, FiUsers } from 'react-icons/fi';

import '../assets/web/assets/mobirise-icons2/mobirise2.css';
import '../assets/bootstrap/css/bootstrap.min.css';
import '../assets/bootstrap/css/bootstrap-grid.min.css';
import '../assets/bootstrap/css/bootstrap-reboot.min.css';
import '../assets/parallax/jarallax.css';
import '../assets/web/assets/gdpr-plugin/gdpr-styles.css';
import '../assets/dropdown/css/style.css';
import '../assets/socicon/css/styles.css';
import '../assets/theme/css/style.css';
import '../assets/mobirise/css/mbr-additional.css';
import '../assets/mobirise/css/pdf.css';

import Navbar from './components/Navbar';
import Footer from './components/Footer';

import integridade from '../assets/integridade.pdf';
import privacy from '../assets/privacidade.pdf';
import brind from '../assets/brindes.pdf';
import relashioship from '../assets/relashioship.pdf';
import fachada from './assets/images/6-768x1024.jpg';
import transmissao from './assets/images/3.jpg';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const sectionReveal = {
  initial: 'hidden',
  whileInView: 'show',
  viewport: { once: true, amount: 0.18 },
  variants: fadeUp,
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

function SectionTag({ children, light = false }) {
  return (
    <span
      style={{
        display: 'inline-block',
        marginBottom: '14px',
        fontSize: '0.82rem',
        fontWeight: 700,
        letterSpacing: '1.4px',
        textTransform: 'uppercase',
        color: light ? '#d6a04a' : '#b7832f',
      }}
    >
      {children}
    </span>
  );
}

function SectionTitle({ children, center = false, light = false, maxWidth = '760px' }) {
  return (
    <h2
      style={{
        fontSize: 'clamp(2rem, 3vw, 3.2rem)',
        lineHeight: 1.12,
        fontWeight: 800,
        letterSpacing: '-0.8px',
        color: light ? '#ffffff' : '#0d2e63',
        marginBottom: '18px',
        maxWidth,
        marginLeft: center ? 'auto' : 0,
        marginRight: center ? 'auto' : 0,
      }}
    >
      {children}
    </h2>
  );
}

function SectionText({ children, center = false, light = false, maxWidth = '760px' }) {
  return (
    <p
      style={{
        fontSize: '1.04rem',
        lineHeight: 1.9,
        color: light ? 'rgba(255,255,255,0.78)' : '#5f7088',
        maxWidth,
        margin: center ? '0 auto' : 0,
      }}
    >
      {children}
    </p>
  );
}

function Etica() {
  const documents = useMemo(
    () => [
      {
        id: 'conduta',
        category: 'Código',
        title: 'Código de Conduta',
        subtitle: 'Diretrizes para comportamento e tomada de decisão',
        description:
          'Documento que orienta a atuação ética da RealEnergy e estabelece parâmetros de conduta para colaboradores, parceiros, fornecedores, clientes e demais públicos de relacionamento.',
        file: integridade,
      },
      {
        id: 'privacidade',
        category: 'Privacidade',
        title: 'Política de Privacidade',
        subtitle: 'Tratamento responsável de dados pessoais',
        description:
          'Apresenta as diretrizes relacionadas ao tratamento de dados pessoais, reforçando o compromisso da empresa com segurança da informação, transparência e conformidade com a legislação aplicável.',
        file: privacy,
      },
      {
        id: 'brindes',
        category: 'Integridade',
        title: 'Política de Brindes e Hospitalidades',
        subtitle: 'Relações profissionais com responsabilidade',
        description:
          'Define critérios para oferta, recebimento e registro de brindes, hospitalidades e cortesias, prevenindo conflitos de interesse e fortalecendo práticas éticas.',
        file: brind,
      },
      {
        id: 'poder-publico',
        category: 'Relacionamento institucional',
        title: 'Política de Relacionamento com o Poder Público',
        subtitle: 'Conformidade na interação institucional',
        description:
          'Estabelece diretrizes para interações com agentes públicos, doações, patrocínios e processos administrativos, promovendo integridade e segurança institucional.',
        file: relashioship,
      },
    ],
    []
  );

  const [activeDocId, setActiveDocId] = useState(documents[0].id);
  const activeDoc = documents.find((doc) => doc.id === activeDocId) || documents[0];

  const highlightCards = [
    {
      title: 'Ética e integridade',
      text: 'A base das decisões e dos relacionamentos institucionais da RealEnergy.',
      icon: <FiShield size={22} />,
    },
    {
      title: 'Transparência',
      text: 'Compromisso com práticas responsáveis, critérios objetivos e comunicação clara.',
      icon: <FiFileText size={22} />,
    },
    {
      title: 'Respeito e inclusão',
      text: 'Ambiente profissional orientado por igualdade, responsabilidade e valorização das pessoas.',
      icon: <FiUsers size={22} />,
    },
  ];

  const previewUrl = `${activeDoc.file}#toolbar=1&navpanes=0&scrollbar=1&view=FitH`;

  return (
    <div
      style={{
        background: 'linear-gradient(180deg, #f3f6fb 0%, #ffffff 30%, #f7f9fc 100%)',
        color: '#11233e',
        fontFamily: 'Arial, sans-serif',
        overflow: 'hidden',
      }}
    >
      <Navbar />

      <section
        style={{
          position: 'relative',
          minHeight: '72vh',
          display: 'flex',
          alignItems: 'center',
          backgroundImage: `linear-gradient(95deg, rgba(8,20,41,0.95) 0%, rgba(10,33,70,0.9) 42%, rgba(13,52,110,0.68) 100%), url(${fachada})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 15% 20%, rgba(255,255,255,0.08), transparent 22%), radial-gradient(circle at 85% 12%, rgba(214,160,74,0.18), transparent 16%)',
          }}
        />

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="row align-items-center g-5">
            <div className="col-lg-7">
              <motion.div {...sectionReveal} style={{ maxWidth: '760px', padding: '50px 0' }}>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 16px',
                    borderRadius: '999px',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    color: 'rgba(255,255,255,0.84)',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    marginBottom: '24px',
                  }}
                >
                  Compliance e integridade
                </div>

                <h1
                  style={{
                    fontSize: 'clamp(2.5rem, 5vw, 4.7rem)',
                    lineHeight: 1.03,
                    fontWeight: 800,
                    letterSpacing: '-1.2px',
                    color: '#ffffff',
                    marginBottom: '22px',
                  }}
                >
                  Ética, transparência e responsabilidade como pilares da nossa atuação.
                </h1>

                <p
                  style={{
                    fontSize: '1.08rem',
                    lineHeight: 1.9,
                    color: 'rgba(255,255,255,0.82)',
                    maxWidth: '650px',
                  }}
                >
                  A RealEnergy conduz suas atividades com base em integridade, respeito e conformidade,
                  fortalecendo relações profissionais sustentadas por critérios claros, segurança institucional
                  e compromisso com a sociedade.
                </p>
              </motion.div>
            </div>

            <div className="col-lg-5">
              <motion.div
                initial={{ opacity: 0, y: 34 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.08 }}
                style={{ position: 'relative', maxWidth: '500px', marginLeft: 'auto' }}
              >
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                  style={{
                    position: 'relative',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '30px',
                    padding: '18px',
                    backdropFilter: 'blur(16px)',
                    boxShadow: '0 30px 80px rgba(0,0,0,0.24)',
                  }}
                >
                  <img
                    src={transmissao}
                    alt="Governança e integridade"
                    style={{
                      width: '100%',
                      height: '420px',
                      objectFit: 'cover',
                      borderRadius: '24px',
                      display: 'block',
                    }}
                  />

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.25 }}
                    whileHover={{ y: -4 }}
                    style={{
                      position: 'absolute',
                      left: '-20px',
                      bottom: '34px',
                      width: '255px',
                      background: 'rgba(255,255,255,0.97)',
                      borderRadius: '22px',
                      padding: '18px',
                      boxShadow: '0 20px 55px rgba(15, 23, 42, 0.18)',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.78rem',
                        color: '#b7832f',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        marginBottom: '8px',
                        fontWeight: 700,
                      }}
                    >
                      Governança
                    </div>
                    <div
                      style={{
                        fontWeight: 800,
                        fontSize: '1.12rem',
                        marginBottom: '6px',
                        color: '#0d2e63',
                        lineHeight: 1.3,
                      }}
                    >
                      Diretrizes claras para uma atuação ética e consistente.
                    </div>
                    <div style={{ color: '#607089', fontSize: '0.95rem', lineHeight: 1.6 }}>
                      Documentos organizados e acesso simplificado para consulta institucional.
                    </div>
                  </motion.div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section style={{ marginTop: '-48px', position: 'relative', zIndex: 3 }}>
        <div className="container">
          <motion.div
            {...sectionReveal}
            style={{
              background: 'rgba(255,255,255,0.95)',
              borderRadius: '28px',
              boxShadow: '0 24px 70px rgba(15, 23, 42, 0.08)',
              border: '1px solid rgba(15, 23, 42, 0.05)',
              padding: '30px',
            }}
          >
            <motion.div
              className="row g-4"
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
            >
              {highlightCards.map((item, index) => (
                <div className="col-lg-4" key={index}>
                  <motion.div
                    variants={fadeUp}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    whileHover={{ y: -8, boxShadow: '0 24px 48px rgba(13, 46, 99, 0.08)' }}
                    style={{
                      background: '#ffffff',
                      borderRadius: '22px',
                      padding: '28px 24px',
                      height: '100%',
                      border: '1px solid rgba(13, 46, 99, 0.06)',
                    }}
                  >
                    <div
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #edf3ff, #f7efe1)',
                        marginBottom: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#0d2e63',
                      }}
                    >
                      {item.icon}
                    </div>

                    <h3
                      style={{
                        fontSize: '1.22rem',
                        fontWeight: 800,
                        marginBottom: '12px',
                        color: '#0d2e63',
                      }}
                    >
                      {item.title}
                    </h3>

                    <p style={{ color: '#607089', lineHeight: 1.8, marginBottom: 0 }}>{item.text}</p>
                  </motion.div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section style={{ padding: '110px 0 60px' }}>
        <div className="container">
          <motion.div {...sectionReveal} className="text-center" style={{ maxWidth: '920px', margin: '0 auto 50px' }}>
            <SectionTag>Políticas e documentos</SectionTag>
            <SectionTitle center maxWidth="920px">
              Acesse os documentos de compliance com mais clareza, organização e facilidade de leitura.
            </SectionTitle>
            <SectionText center maxWidth="860px">
              Reunimos as principais políticas institucionais em uma experiência mais simples e profissional,
              com seleção lateral, resumo do conteúdo e visualização direta no navegador.
            </SectionText>
          </motion.div>

          <div className="row g-4 align-items-stretch">
            <div className="col-xl-4">
              <motion.div
                {...sectionReveal}
                style={{
                  background: '#ffffff',
                  borderRadius: '28px',
                  padding: '24px',
                  border: '1px solid rgba(13, 46, 99, 0.06)',
                  boxShadow: '0 20px 50px rgba(15, 23, 42, 0.07)',
                  height: '100%',
                }}
              >
                <div style={{ marginBottom: '18px' }}>
                  <SectionTag>Documentos disponíveis</SectionTag>
                  <SectionTitle maxWidth="100%">Selecione um documento</SectionTitle>
                  <SectionText maxWidth="100%">
                    Escolha o conteúdo que deseja consultar. O documento selecionado será exibido ao lado.
                  </SectionText>
                </div>

                <motion.div
                  style={{ display: 'grid', gap: '14px' }}
                  variants={stagger}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.15 }}
                >
                  {documents.map((doc) => {
                    const isSelected = activeDoc.id === doc.id;

                    return (
                      <motion.button
                        key={doc.id}
                        type="button"
                        variants={fadeUp}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        whileHover={{ y: -4 }}
                        onClick={() => setActiveDocId(doc.id)}
                        style={{
                          textAlign: 'left',
                          border: isSelected
                            ? '1px solid rgba(13, 79, 179, 0.18)'
                            : '1px solid rgba(13, 46, 99, 0.06)',
                          background: isSelected ? '#f4f8ff' : '#ffffff',
                          borderRadius: '20px',
                          padding: '18px',
                          boxShadow: isSelected
                            ? '0 18px 40px rgba(13, 79, 179, 0.08)'
                            : '0 8px 22px rgba(15, 23, 42, 0.04)',
                          cursor: 'pointer',
                          transition: 'all 0.25s ease',
                        }}
                      >
                        <div
                          style={{
                            fontSize: '0.76rem',
                            fontWeight: 700,
                            letterSpacing: '1.2px',
                            textTransform: 'uppercase',
                            color: '#b7832f',
                            marginBottom: '8px',
                          }}
                        >
                          {doc.category}
                        </div>

                        <div
                          style={{
                            fontSize: '1.04rem',
                            fontWeight: 800,
                            color: isSelected ? '#0d4fb3' : '#0d2e63',
                            lineHeight: 1.35,
                            marginBottom: '6px',
                          }}
                        >
                          {doc.title}
                        </div>

                        <div
                          style={{
                            color: '#607089',
                            lineHeight: 1.7,
                            fontSize: '0.93rem',
                            marginBottom: '8px',
                          }}
                        >
                          {doc.subtitle}
                        </div>

                        <div
                          style={{
                            color: '#607089',
                            lineHeight: 1.7,
                            fontSize: '0.92rem',
                          }}
                        >
                          {doc.description}
                        </div>
                      </motion.button>
                    );
                  })}
                </motion.div>
              </motion.div>
            </div>

            <div className="col-xl-8">
              <motion.div
                {...sectionReveal}
                style={{
                  background: '#ffffff',
                  borderRadius: '28px',
                  padding: '24px',
                  border: '1px solid rgba(13, 46, 99, 0.06)',
                  boxShadow: '0 20px 50px rgba(15, 23, 42, 0.07)',
                  height: '100%',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: '18px',
                    flexWrap: 'wrap',
                    marginBottom: '22px',
                  }}
                >
                  <div style={{ maxWidth: '720px' }}>
                    <div
                      style={{
                        fontSize: '0.76rem',
                        fontWeight: 700,
                        letterSpacing: '1.2px',
                        textTransform: 'uppercase',
                        color: '#b7832f',
                        marginBottom: '8px',
                      }}
                    >
                      Documento selecionado
                    </div>

                    <h3
                      style={{
                        fontSize: '1.5rem',
                        fontWeight: 800,
                        color: '#0d2e63',
                        marginBottom: '8px',
                      }}
                    >
                      {activeDoc.title}
                    </h3>

                    <p
                      style={{
                        color: '#607089',
                        lineHeight: 1.8,
                        marginBottom: '8px',
                      }}
                    >
                      {activeDoc.description}
                    </p>

                    <p
                      style={{
                        color: '#7a8798',
                        lineHeight: 1.7,
                        marginBottom: 0,
                        fontSize: '0.94rem',
                      }}
                    >
                      Caso a visualização não seja suportada no dispositivo ou navegador, utilize as opções de baixar o arquivo ou abrir em nova guia.
                    </p>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      gap: '12px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <motion.a
                      whileHover={{ y: -2, scale: 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      href={activeDoc.file}
                      download
                      style={{
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        padding: '14px 18px',
                        borderRadius: '14px',
                        background: 'linear-gradient(135deg, #0d4fb3, #0a3d91)',
                        color: '#ffffff',
                        fontWeight: 700,
                        boxShadow: '0 18px 36px rgba(13, 79, 179, 0.2)',
                      }}
                    >
                      <AiOutlineDownload size={18} />
                      Baixar PDF
                    </motion.a>

                    <motion.a
                      whileHover={{ y: -2, scale: 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      href={activeDoc.file}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        padding: '14px 18px',
                        borderRadius: '14px',
                        background: '#f4f8ff',
                        color: '#0d4fb3',
                        fontWeight: 700,
                        border: '1px solid rgba(13, 79, 179, 0.12)',
                      }}
                    >
                      <FiExternalLink size={17} />
                      Abrir em nova guia
                    </motion.a>
                  </div>
                </div>

                <div
                  style={{
                    borderRadius: '24px',
                    overflow: 'hidden',
                    border: '1px solid rgba(13, 46, 99, 0.08)',
                    background: '#f7f9fc',
                    minHeight: '780px',
                  }}
                >
                  <iframe
                    key={activeDoc.id}
                    title={activeDoc.title}
                    src={previewUrl}
                    style={{
                      width: '100%',
                      height: '780px',
                      border: 'none',
                      display: 'block',
                      background: '#ffffff',
                    }}
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: '0 0 100px' }}>
        <div className="container">
          <motion.div
            {...sectionReveal}
            style={{
              background: 'linear-gradient(135deg, #ffffff, #f8fbff)',
              borderRadius: '30px',
              padding: '38px',
              boxShadow: '0 24px 60px rgba(15, 23, 42, 0.08)',
              border: '1px solid rgba(15, 23, 42, 0.05)',
            }}
          >
            <div className="row g-4 align-items-center">
              <div className="col-lg-7">
                <SectionTag>Transparência e igualdade salarial</SectionTag>
                <SectionTitle maxWidth="680px">
                  Relações profissionais pautadas por respeito, critérios objetivos e responsabilidade institucional.
                </SectionTitle>
                <SectionText maxWidth="700px">
                  A RealEnergy reafirma seu compromisso com a ética, a integridade e a transparência também nas relações de trabalho,
                  adotando critérios objetivos de remuneração e promovendo ações voltadas à diversidade, à inclusão e ao fortalecimento
                  de um ambiente profissional justo e respeitoso.
                </SectionText>
              </div>

              <div className="col-lg-5">
                <motion.div
                  whileHover={{ y: -6 }}
                  style={{
                    background: '#ffffff',
                    borderRadius: '24px',
                    padding: '24px',
                    border: '1px solid rgba(13, 46, 99, 0.06)',
                    boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)',
                  }}
                >
                  <div
                    style={{
                      fontSize: '1.08rem',
                      fontWeight: 800,
                      color: '#0d2e63',
                      marginBottom: '10px',
                    }}
                  >
                    Compromisso institucional
                  </div>
                  <p style={{ color: '#607089', lineHeight: 1.8, marginBottom: 0 }}>
                    Diretrizes claras e relacionamento responsável com colaboradores, clientes, fornecedores, parceiros e sociedade.
                  </p>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section style={{ padding: '0 0 110px' }}>
        <div className="container">
          <motion.div
            {...sectionReveal}
            style={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '34px',
              padding: '64px 30px',
              backgroundImage: `linear-gradient(135deg, rgba(9,28,59,0.94), rgba(14,63,148,0.9)), url(${transmissao})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              textAlign: 'center',
              boxShadow: '0 28px 72px rgba(15, 23, 42, 0.16)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at top center, rgba(255,255,255,0.1), transparent 28%)',
              }}
            />

            <div style={{ maxWidth: '820px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
              <SectionTag light>Canal de comunicação</SectionTag>
              <SectionTitle light center maxWidth="820px">
                Utilize o canal de denúncia com segurança, sigilo e responsabilidade.
              </SectionTitle>
              <SectionText light center maxWidth="760px">
                A RealEnergy disponibiliza um canal dedicado para o registro de relatos e ocorrências.
                As manifestações são tratadas com imparcialidade, confidencialidade e possibilidade de anonimato,
                reforçando o compromisso da empresa com a integridade.
              </SectionText>

              <motion.a
                whileHover={{ y: -2, scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                href="mailto:denuncia@realenergy.com.br"
                style={{
                  marginTop: '28px',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  padding: '15px 22px',
                  borderRadius: '14px',
                  background: '#ffffff',
                  color: '#0d4fb3',
                  fontWeight: 700,
                  boxShadow: '0 16px 32px rgba(0,0,0,0.12)',
                }}
              >
                <FiMail size={18} />
                denuncia@realenergy.com.br
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Etica;